import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import isNil from 'lodash/isNil';
import { CohortSampleContextType } from '../../../../../redux/slices/oldBuilder';
import { SAMPLE_PERCENTAGE_VALUES } from '../../../../../config/constants';
import ExclusiveButtonGroup from '../../../../../components/ExclusiveButtonGroup';
import ThinDivider from '../../../../../components/ThinDivider';
import { NumberInputField } from '../../../../../components/Form';
import { SvgIcon } from '../../../../../components/Icon';
import { floatingComponentStyles } from '../../../../../styles/shareableStyles';
import { StyledSingleSelectOption } from '../../../../../components/Dropdown/SingleSelect';

interface IProps {
  sampleContextType: CohortSampleContextType;
  sampleValue: number | null;
  onChange: (
    contextType: CohortSampleContextType,
    newValue: number | null,
  ) => void;
}

const ABSOLUTE_ITEM_LIST = [10, 50, 100, 500, 1000, 5000];
const ABSOLUTE_LIMIT = 5000;
const PERCENTAGE_LIMIT = 100;

const contextTypeOptions = [
  { label: 'Percentage', value: CohortSampleContextType.Percentage },
  { label: 'Absolute', value: CohortSampleContextType.Absolute },
];

const Container = styled.div`
  width: 160px;
  ${floatingComponentStyles};
`;
const ExclusiveOptionsContainer = styled.div`
  padding: 2px;
`;
const NumberInput = styled(NumberInputField)`
  width: 100%;
  > input {
    border: 0;
    width: 100%;
  }
`;
const SaveIcon = styled(SvgIcon)`
  position: absolute;
  right: 4px;
  top: 50%;
  cursor: pointer;
  transform: translateY(-50%);
`;

const BuilderInspectConfigMenu = ({
  sampleContextType,
  sampleValue,
  onChange,
}: IProps) => {
  const [contextType, setContextType] =
    useState<CohortSampleContextType>(sampleContextType);
  const [numberInput, setNumberInput] = useState<number | null>(null);

  const isPercentage = contextType === CohortSampleContextType.Percentage;
  const max = isPercentage ? PERCENTAGE_LIMIT : ABSOLUTE_LIMIT;

  const handleSelectOption = useCallback(
    (newValue: number) => {
      onChange(contextType, newValue);
    },
    [contextType, onChange],
  );
  const handleSubmitCustomValue = () => {
    if (!isNil(numberInput) && numberInput <= max) {
      onChange(contextType, numberInput);
    }
  };

  return (
    <Container>
      <ExclusiveOptionsContainer>
        <ExclusiveButtonGroup
          options={contextTypeOptions}
          selectedValue={contextType}
          onChange={setContextType}
        />
      </ExclusiveOptionsContainer>
      <ThinDivider layout="horizontal" size="full" />
      <NumberInput
        placeholder={'Enter a value'}
        min={0}
        max={max}
        value={numberInput}
        onChange={setNumberInput}
        suffixAction={
          !isNil(numberInput) && (
            <SaveIcon
              name="circle-check"
              width={14}
              height={14}
              onClick={handleSubmitCustomValue}
              isHoverable={true}
            />
          )
        }
      />
      <ThinDivider layout="horizontal" size="full" />
      {(isPercentage ? SAMPLE_PERCENTAGE_VALUES : ABSOLUTE_ITEM_LIST).map(
        (item) => (
          <StyledSingleSelectOption
            key={item}
            onClick={() => handleSelectOption(item)}
            isActive={sampleContextType === contextType && item === sampleValue}
          >
            {item}
            {isPercentage && '%'}
          </StyledSingleSelectOption>
        ),
      )}
    </Container>
  );
};

export default BuilderInspectConfigMenu;
